import escapeStringRegexp from 'escape-string-regexp';
import { runInAction } from 'mobx';

import ComposicaoUtil from './ComposicaoUtil';

export const PREFIXO_IMPEDIMENTO = 'impedimento averbado pel';

const REGEX_QUALQUER_OBSERVACAO = /\n?Observação \d+: [\s\S]*?(?=\nObservação \d+:|$)/g;
const REGEX_OBSERVACAO_AUSENCIA = /\n?Observação \d+: ausentes?, justificadamente, [\s\S]*?(?=\nObservação \d+:|$)/g;
const REGEX_CABECALHO_OBSERVACAO = /^\n?Observação \d+: /;

const OPCOES_PADRAO = {
    atualizarRascunho: true,
    atualizarDecisao: false,
    permitirCertidaoAssinada: false,
    ignorarPendenteImportacao: true,
    obterDecisao: null,
    aoAlterarDecisao: null
};

export default class ObservacaoCertidaoUtil {
    static adicionarObservacao(textoBase, conteudo, salaSessaoStore) {
        const texto = textoBase || '';

        if (!conteudo || texto.includes(conteudo)) {
            return texto;
        }

        const numeroObservacao = salaSessaoStore.getQuantidadeObservacoes(texto) + 1;
        const base = salaSessaoStore.renumerarObservacoes(texto).trimEnd();

        return ComposicaoUtil.normalizarLinhasEmBranco(
            (base ? base + '\n\n' : '\n') + 'Observação ' + numeroObservacao + ': ' + conteudo + '.\n'
        );
    }

    static removerObservacao(textoBase, conteudo, salaSessaoStore) {
        if (!textoBase || !conteudo || !textoBase.includes(conteudo)) {
            return textoBase;
        }

        let novoTexto = textoBase.replaceAll(REGEX_QUALQUER_OBSERVACAO, (observacao) =>
            observacao.includes(conteudo) ? '' : observacao
        );

        if (novoTexto.includes(conteudo)) {
            novoTexto = novoTexto.replace(conteudo, '');
        }

        return ComposicaoUtil.normalizarLinhasEmBranco(salaSessaoStore.renumerarObservacoes(novoTexto));
    }

    static removerImpedimentoDeMagistrados(textoBase, nomesNaoImpedidos, salaSessaoStore) {
        if (!textoBase || !nomesNaoImpedidos || nomesNaoImpedidos.length === 0) {
            return textoBase;
        }

        let novoTexto = textoBase;

        for (const nome of nomesNaoImpedidos) {
            const escapedNome = escapeStringRegexp(nome);
            // eslint-disable-next-line security/no-unsafe-regexp
            const regex = new RegExp(
                String.raw`\n?Observação \d+: ${escapeStringRegexp(PREFIXO_IMPEDIMENTO)}[ao] ${escapedNome}\.(?=\n|$)`,
                'g'
            );
            novoTexto = novoTexto.replaceAll(regex, '');
        }

        return novoTexto === textoBase
            ? textoBase
            : ComposicaoUtil.normalizarLinhasEmBranco(salaSessaoStore.renumerarObservacoes(novoTexto));
    }

    static removerObservacoesAusencia(textoBase, conteudoManter, salaSessaoStore) {
        if (!textoBase) {
            return textoBase;
        }

        const novoTexto = textoBase.replaceAll(REGEX_OBSERVACAO_AUSENCIA, (observacao) => {
            const conteudo = observacao.replace(REGEX_CABECALHO_OBSERVACAO, '').trimEnd().replace(/\.$/, '');

            return conteudoManter && conteudo === conteudoManter ? observacao : '';
        });

        return novoTexto === textoBase
            ? textoBase
            : ComposicaoUtil.normalizarLinhasEmBranco(salaSessaoStore.renumerarObservacoes(novoTexto));
    }

    static normalizarMagistrados(lista) {
        return (lista || []).map((item) => item?.magistrado ?? item).filter(Boolean);
    }

    static nomeComTratamento(magistrado, salaSessaoStore) {
        return ComposicaoUtil.montarNomeComTratamento(magistrado, salaSessaoStore);
    }

    static nomesRemovidos(magistradosNovos, composicaoAnterior, salaSessaoStore) {
        const novos = this.normalizarMagistrados(magistradosNovos);

        return this.normalizarMagistrados(composicaoAnterior)
            .filter((anterior) => novos.every((novo) => !(novo.sigla === anterior.sigla && novo.cpf === anterior.cpf)))
            .map((anterior) => this.nomeComTratamento(anterior, salaSessaoStore));
    }

    static montarTextoAusencia(magistrados, dependencias) {
        const ausentes = this.normalizarMagistrados(magistrados).filter((m) => m.ausente);

        if (ausentes.length === 0) {
            return null;
        }

        const antiguidade = ComposicaoUtil.sortObject(dependencias.sessaoStore.sessaoSelecionada.antiguidade);
        const posicao = (magistrado) => {
            const indice = antiguidade.findIndex((item) => item.siglaMagistrado === magistrado.sigla);
            return indice === -1 ? Number.MAX_SAFE_INTEGER : indice;
        };

        const nomes = ausentes
            .toSorted((a, b) => posicao(a) - posicao(b))
            .map((m) => this.nomeComTratamento(m, dependencias.salaSessaoStore));

        const lista = nomes.length > 1 ? nomes.slice(0, -1).join(', ') + ' e ' + nomes.at(-1) : nomes[0];

        return (nomes.length > 1 ? 'ausentes' : 'ausente') + ', justificadamente, ' + lista;
    }

    static montarTextosImpedimento(magistrados, dependencias) {
        return this.normalizarMagistrados(magistrados)
            .filter((m) => m.situacaoImpedimento)
            .map((m) => {
                const artigo = m.genero === 'F' ? 'a ' : 'o ';
                return PREFIXO_IMPEDIMENTO + artigo + this.nomeComTratamento(m, dependencias.salaSessaoStore);
            });
    }

    static montarTextoPresidente(magistrado, dependencias) {
        return (
            dependencias.salaSessaoStore.pronomeTratamentoComCargo(magistrado) +
            ' ' +
            magistrado.nome.trim() +
            ', Presidente, assinará o acórdão nos termos do art. 165, parágrafo único, do Regimento Interno do Tribunal Superior do Trabalho'
        );
    }

    static transformarAusencia(magistrados, composicaoAnterior, dependencias) {
        const lista = this.normalizarMagistrados(magistrados);
        const textoAusentes = this.montarTextoAusencia(lista, dependencias);
        const nomesParaLimpar = [
            ...lista.filter((m) => !m.ausente).map((m) => this.nomeComTratamento(m, dependencias.salaSessaoStore)),
            ...this.nomesRemovidos(lista, composicaoAnterior, dependencias.salaSessaoStore)
        ];

        return (texto) => {
            let novoTexto = ComposicaoUtil.removerAusenciaMagistrados(
                texto,
                nomesParaLimpar,
                dependencias.salaSessaoStore
            );

            novoTexto = this.removerObservacoesAusencia(novoTexto, textoAusentes, dependencias.salaSessaoStore);

            if (textoAusentes) {
                novoTexto = this.adicionarObservacao(novoTexto, textoAusentes, dependencias.salaSessaoStore);
            }
            return novoTexto;
        };
    }

    static transformarImpedimento(magistrados, composicaoAnterior, dependencias) {
        const lista = this.normalizarMagistrados(magistrados);
        const textosImpedimento = this.montarTextosImpedimento(lista, dependencias);
        const nomesParaLimpar = [
            ...lista
                .filter((m) => !m.situacaoImpedimento)
                .map((m) => this.nomeComTratamento(m, dependencias.salaSessaoStore)),
            ...this.nomesRemovidos(lista, composicaoAnterior, dependencias.salaSessaoStore)
        ];

        return (texto) => {
            let novoTexto = this.removerImpedimentoDeMagistrados(texto, nomesParaLimpar, dependencias.salaSessaoStore);

            for (const textoImpedimento of textosImpedimento) {
                novoTexto = this.adicionarObservacao(novoTexto, textoImpedimento, dependencias.salaSessaoStore);
            }
            return novoTexto;
        };
    }

    static transformarInclusao(conteudo, dependencias) {
        return (texto) => this.adicionarObservacao(texto, conteudo, dependencias.salaSessaoStore);
    }

    static transformarExclusao(conteudo, dependencias) {
        return (texto) => this.removerObservacao(texto, conteudo, dependencias.salaSessaoStore);
    }

    static opcoesAlteracaoComposicao(registro, atualizarCertidao) {
        return {
            atualizarRascunho: !registro.certidaoGerada || Boolean(atualizarCertidao),
            atualizarDecisao: Boolean(atualizarCertidao)
        };
    }

    static compor(transformacoes) {
        const lista = [transformacoes].flat().filter(Boolean);

        return (texto) => {
            let resultado = texto;

            for (const transformar of lista) {
                resultado = transformar(resultado);
            }

            return resultado === texto ? texto : ComposicaoUtil.normalizarLinhasEmBranco(resultado);
        };
    }

    static async prepararTextos(registro, transformacoes, dependencias, opcoes = {}) {
        const config = { ...OPCOES_PADRAO, ...opcoes };
        const plano = {
            config,
            mudou: false,
            persistido: { rascunho: false, decisao: false },
            rascunho: { aplicavel: false, antigo: '', novo: '', mudou: false },
            decisao: { aplicavel: false, antiga: '', nova: '', mudou: false }
        };

        if (config.ignorarPendenteImportacao && registro.necessitaImportacaoSalaSessao === true) {
            return plano;
        }

        const aplicar = this.compor(transformacoes);

        if (config.atualizarRascunho) {
            const antigo =
                registro.rascunhoCertidao ??
                ((await dependencias.certidaoService.recuperarRascunhoCertidao(registro)) || '');
            const novo = aplicar(antigo);

            plano.rascunho = { aplicavel: true, antigo, novo, mudou: novo !== antigo };
        }

        const decisaoAplicavel =
            config.atualizarDecisao &&
            registro.certidaoGerada &&
            (config.permitirCertidaoAssinada || !registro.certidaoJulgamentoAssinada);

        if (decisaoAplicavel) {
            const obterDecisao =
                config.obterDecisao ??
                ((reg) =>
                    ComposicaoUtil.obterDecisaoCertidao(reg, dependencias.sessaoStore, dependencias.certidaoService));
            const antiga = (await obterDecisao(registro)) || '';
            const nova = aplicar(antiga);

            plano.decisao = { aplicavel: true, antiga, nova, mudou: nova !== antiga };
        }

        plano.mudou = plano.rascunho.mudou || plano.decisao.mudou;

        return plano;
    }

    static async persistirTextos(registro, plano, dependencias) {
        if (plano.rascunho.mudou) {
            runInAction(() => (registro.rascunhoEmEdicao = true));
            await dependencias.salaSessaoStore.salvarRascunhoCertidao(registro, plano.rascunho.novo);
            runInAction(() => (registro.rascunhoCertidao = plano.rascunho.novo));
            plano.persistido.rascunho = true;
        }

        if (plano.decisao.mudou) {
            await dependencias.certidaoService.salvarDecisao(registro, plano.decisao.nova);
            plano.persistido.decisao = true;
            plano.config.aoAlterarDecisao?.(plano.decisao.nova);
        }

        return plano;
    }

    static async reverterTextos(registro, plano, dependencias) {
        if (plano.persistido.decisao) {
            try {
                await dependencias.certidaoService.salvarDecisao(registro, plano.decisao.antiga);
                plano.persistido.decisao = false;
                plano.config.aoAlterarDecisao?.(plano.decisao.antiga);
            } catch (error) {
                console.error('Falha ao reverter a decisão da certidão', error);
            }
        }

        if (plano.persistido.rascunho) {
            try {
                await dependencias.salaSessaoStore.salvarRascunhoCertidao(registro, plano.rascunho.antigo);
                runInAction(() => (registro.rascunhoCertidao = plano.rascunho.antigo));
                plano.persistido.rascunho = false;
            } catch (error) {
                console.error('Falha ao reverter o rascunho da certidão', error);
            }
        }
    }

    static async atualizarTextosCertidao(registro, transformacoes, dependencias, opcoes = {}) {
        const plano = await this.prepararTextos(registro, transformacoes, dependencias, opcoes);

        try {
            await this.persistirTextos(registro, plano, dependencias);
        } catch (error) {
            await this.reverterTextos(registro, plano, dependencias);
            throw error;
        }

        return plano;
    }
}
